// src/components/onboarding/onboarding-page.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, ChevronLeft, ChevronDown, ChevronRight, Loader2, AlertCircle,
  Calendar, RefreshCw, Search, ArrowRight, Building2, Rocket,
  Clock, CircleDot, CheckCircle2, Lock,
} from "lucide-react";
import { toast } from "sonner";

import {
  fetchStageDefinitions,
  listCustomers,
  getCustomer,
  createCustomer,
  runStage7,
  runStage8,
  runStage9,
  runStage10BrandMonitoring,
  type StageDefinition,
  type CustomerSummary,
  type CustomerDetail,
  type IntakePayload,
} from "@/api/onboarding-api";
import { OnboardingStepper } from "./onboarding-stepper";
import { OnboardingStageContent } from "./onboarding-stage-content";
import { CompareRunsPage } from "./compare-runs-page";

interface OnboardingPageProps {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (c: boolean) => void;
  onBackToSearch?: () => void;
}

type CustomerGroup = "in_progress" | "not_started" | "completed";

// ⚠️ "Effective stage" — once a customer exists, intake (Stage 1) is implicitly
// done, so the UI treats any customer at current_stage=1 as if they were at
// Stage 2 (framework generation). This handles legacy customers created
// before the backend started advancing current_stage to 2 after intake.
function effectiveStageNumeric(currentStage: number): number {
  return Math.max(2, currentStage);
}

function classifyCustomer(c: CustomerSummary): CustomerGroup {
  if (c.status === "completed") return "completed";
  // A customer is "not started" only if they have NO stage yet (which
  // shouldn't happen — but defensively guard for it). All created customers
  // have effectively kicked off intake.
  if (c.currentStage < 1) return "not_started";
  return "in_progress";
}

export function OnboardingPage(props: OnboardingPageProps) {
  const [stages, setStages] = useState<StageDefinition[]>([]);
  const [loadingStages, setLoadingStages] = useState(true);

  useEffect(() => {
    fetchStageDefinitions()
      .then((res) => setStages(res.stages))
      .catch((e) => toast.error(e?.message || "Failed to load stage definitions"))
      .finally(() => setLoadingStages(false));
  }, []);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // ⚠️ Compare-runs view state — set by Stage 10's "Compare" button.
  // When set, we render <CompareRunsPage> instead of the customer detail.
  // Clearing it returns to the customer detail (which still has selectedId).
  const [compareSpec, setCompareSpec] = useState<{
    customerId: number;
    baselineRunId: number;
    candidateRunId: number;
  } | null>(null);

  // We need the customer object to pass to CompareRunsPage for the header.
  // Reuses the same fetch the detail view will have already done.
  const [compareCustomer, setCompareCustomer] = useState<CustomerDetail | null>(null);

  useEffect(() => {
    if (compareSpec) {
      getCustomer(compareSpec.customerId)
        .then(setCompareCustomer)
        .catch((e) => {
          toast.error(e?.message || "Failed to load customer for comparison");
          setCompareSpec(null);
        });
    } else {
      setCompareCustomer(null);
    }
  }, [compareSpec]);

  if (loadingStages) {
    return <FullPageSpinner label="Loading onboarding console…" />;
  }

  // Compare view takes priority over detail view
  if (compareSpec) {
    if (!compareCustomer) {
      return <FullPageSpinner label="Loading comparison…" />;
    }
    return (
      <CompareRunsPage
        customer={compareCustomer}
        runs={compareCustomer.runs || []}
        baselineRunId={compareSpec.baselineRunId}
        candidateRunId={compareSpec.candidateRunId}
        onBack={() => setCompareSpec(null)}
      />
    );
  }

  if (selectedId == null && !showCreateForm) {
    return (
      <CustomerListView
        stages={stages}
        onOpenCustomer={(id) => setSelectedId(id)}
        onNewCustomer={() => setShowCreateForm(true)}
        onBackToSearch={props.onBackToSearch}
      />
    );
  }

  if (showCreateForm) {
    return (
      <CustomerCreateView
        stages={stages}
        onCancel={() => setShowCreateForm(false)}
        onCreated={(id) => {
          setShowCreateForm(false);
          setSelectedId(id);
        }}
      />
    );
  }

  return (
    <CustomerDetailView
      stages={stages}
      customerId={selectedId!}
      onBack={() => setSelectedId(null)}
      onCompareRuns={(baselineRunId, candidateRunId) =>
        setCompareSpec({
          customerId: selectedId!,
          baselineRunId,
          candidateRunId,
        })
      }
    />
  );
}

// ───────────────────────────────────────────────────────────────────────────
// LIST VIEW
// ───────────────────────────────────────────────────────────────────────────

function CustomerListView({
  stages, onOpenCustomer, onNewCustomer, onBackToSearch,
}: {
  stages: StageDefinition[];
  onOpenCustomer: (id: number) => void;
  onNewCustomer: () => void;
  onBackToSearch?: () => void;
}) {
  const [items, setItems] = useState<CustomerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openSections, setOpenSections] = useState<Record<CustomerGroup, boolean>>({
    in_progress: true,
    not_started: true,
    completed: true,
  });

  const load = () => {
    setLoading(true);
    listCustomers({ limit: 200 })
      .then((res) => setItems(res.items))
      .catch((e) => toast.error(e?.message || "Failed to load customers"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(
      (i) =>
        i.brandName.toLowerCase().includes(q) ||
        i.websiteUrl.toLowerCase().includes(q),
    );
  }, [items, query]);

  const grouped = useMemo(() => {
    const byGroup: Record<CustomerGroup, CustomerSummary[]> = {
      in_progress: [], not_started: [], completed: [],
    };
    for (const c of filtered) byGroup[classifyCustomer(c)].push(c);
    (Object.keys(byGroup) as CustomerGroup[]).forEach((k) => {
      byGroup[k].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
    });
    return byGroup;
  }, [filtered]);

  const totalCounts = useMemo(() => {
    const byGroup: Record<CustomerGroup, number> = {
      in_progress: 0, not_started: 0, completed: 0,
    };
    for (const c of items) byGroup[classifyCustomer(c)] += 1;
    return byGroup;
  }, [items]);

  const toggleSection = (g: CustomerGroup) =>
    setOpenSections((p) => ({ ...p, [g]: !p[g] }));

  const groupConfigs: Array<{
    key: CustomerGroup;
    title: string;
    description: string;
    icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
    color: string;
    bg: string;
  }> = [
    {
      key: "in_progress",
      title: "In progress",
      description: "Currently working through the pipeline",
      icon: Loader2,
      color: "var(--alpha-teal)",
      bg: "var(--alpha-teal-10)",
    },
    {
      key: "not_started",
      title: "Not started",
      description: "Created but framework generation hasn't kicked off",
      icon: CircleDot,
      color: "var(--chart-3)",
      bg: "rgba(245,158,11,0.1)",
    },
    {
      key: "completed",
      title: "Completed",
      description: "Full pipeline done, scheduled for continuous re-measurement",
      icon: CheckCircle2,
      color: "var(--chart-3)",
      bg: "rgba(34,197,94,0.1)",
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--background)" }}>
      <div className="w-full px-4 md:px-8 py-6 md:py-10">

        {onBackToSearch && (
          <button
            onClick={onBackToSearch}
            className="flex items-center gap-1.5 mb-4 px-2.5 py-1.5 rounded-md transition-colors"
            style={{
              background: "transparent",
              color: "var(--muted-foreground)",
              fontSize: "var(--text-xs)",
              border: "1px solid var(--border)",
              cursor: "pointer",
            }}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Back to brand search
          </button>
        )}

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: "var(--alpha-teal-10)",
                border: "1px solid var(--alpha-teal-20)",
              }}
            >
              <Rocket className="w-5 h-5" style={{ color: "var(--alpha-teal)" }} />
            </div>
            <div>
              <div className="text-xs uppercase mb-1"
                style={{ color: "var(--alpha-teal)", letterSpacing: "0.08em", fontWeight: 600 }}>
                Admin · Customer Onboarding
              </div>
              <h1 style={{ fontSize: "var(--text-2xl)", fontWeight: 700, color: "var(--foreground)", margin: 0 }}>
                Onboarding pipeline
              </h1>
              <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 4 }}>
                Run new customers through the 10-stage perception measurement pipeline.
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={load}
              className="flex items-center gap-2 px-3 py-2 rounded-lg"
              style={{
                backgroundColor: "var(--muted)", color: "var(--foreground)",
                fontSize: "var(--text-sm)", fontWeight: 500,
                border: "1px solid var(--border)", cursor: "pointer",
              }}>
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button onClick={onNewCustomer}
              className="flex items-center gap-2 px-4 py-2 rounded-lg"
              style={{
                backgroundColor: "var(--alpha-teal)", color: "white",
                fontSize: "var(--text-sm)", fontWeight: 600,
                border: "none", cursor: "pointer",
              }}>
              <Plus className="w-4 h-4" />
              New customer
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 md:gap-3 mb-5">
          {groupConfigs.map((g) => {
            const Icon = g.icon;
            return (
              <div key={g.key} className="p-3 md:p-4 rounded-xl"
                style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-md flex items-center justify-center"
                    style={{ backgroundColor: g.bg }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: g.color }} />
                  </div>
                  <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", fontWeight: 600 }}>
                    {g.title}
                  </span>
                </div>
                <div style={{ fontSize: "var(--text-2xl)", fontWeight: 700, color: "var(--foreground)", lineHeight: 1 }}>
                  {totalCounts[g.key]}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mb-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: "var(--muted-foreground)" }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search customer name or URL…"
              className="w-full"
              style={{
                padding: "10px 12px 10px 36px",
                fontSize: "var(--text-sm)",
                backgroundColor: "var(--card)", color: "var(--foreground)",
                border: "1px solid var(--border)", borderRadius: 8, outline: "none",
              }} />
          </div>
        </div>

        {loading ? (
          <FullPageSpinner inline label="Loading customers…" />
        ) : items.length === 0 ? (
          <EmptyState onNewCustomer={onNewCustomer} />
        ) : (
          <div className="space-y-3">
            {groupConfigs.map((g) => {
              const list = grouped[g.key];
              const isOpen = openSections[g.key];
              if (list.length === 0) return null;
              const Icon = g.icon;
              return (
                <div key={g.key} className="rounded-xl overflow-hidden"
                  style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)" }}>
                  <button onClick={() => toggleSection(g.key)}
                    className="w-full flex items-center justify-between p-3 md:p-4 transition-colors"
                    style={{
                      background: "transparent", cursor: "pointer", border: "none",
                      borderBottom: isOpen ? "1px solid var(--border)" : "none",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "var(--muted)")}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
                    <div className="flex items-center gap-3 min-w-0">
                      {isOpen
                        ? <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "var(--muted-foreground)" }} />
                        : <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: "var(--muted-foreground)" }} />}
                      <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: g.bg }}>
                        <Icon className="w-4 h-4" style={{ color: g.color }} />
                      </div>
                      <div className="min-w-0 text-left">
                        <div className="flex items-center gap-2">
                          <span style={{ fontSize: "var(--text-base)", fontWeight: 600, color: "var(--foreground)" }}>
                            {g.title}
                          </span>
                          <span className="px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: g.bg, color: g.color, fontSize: 11, fontWeight: 700 }}>
                            {list.length}
                          </span>
                        </div>
                        <p className="truncate"
                          style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginTop: 2 }}>
                          {g.description}
                        </p>
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="p-2 md:p-3 space-y-2">
                      {list.map((c) => (
                        <CustomerRow key={c.id} customer={c} stages={stages}
                          onOpen={() => onOpenCustomer(c.id)} groupKey={g.key} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="text-center py-10 rounded-xl"
                style={{ backgroundColor: "var(--card)", border: "1px dashed var(--border)" }}>
                <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                  No customers match "{query}".
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerRow({
  customer, stages, onOpen, groupKey,
}: {
  customer: CustomerSummary;
  stages: StageDefinition[];
  onOpen: () => void;
  groupKey: CustomerGroup;
}) {
  const effective = effectiveStageNumeric(customer.currentStage);
  const stage = stages.find((s) => s.number === effective);
  const totalStages = Math.max(stages.length, 11);
  const progressPct = groupKey === "completed" ? 100 : (effective / totalStages) * 100;
  const accent = groupKey === "completed" ? "var(--chart-3)"
    : groupKey === "not_started" ? "var(--muted-foreground)"
    : "var(--alpha-teal)";

  return (
    <button onClick={onOpen}
      className="w-full text-left p-3 md:p-4 rounded-lg flex flex-col md:flex-row md:items-center gap-3 transition-all"
      style={{ backgroundColor: "var(--background)", border: "1px solid var(--border)", cursor: "pointer" }}
      onMouseOver={(e) => (e.currentTarget.style.borderColor = "var(--alpha-teal-25)")}
      onMouseOut={(e) => (e.currentTarget.style.borderColor = "var(--border)")}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "var(--alpha-teal-10)", color: "var(--alpha-teal)", fontWeight: 700, fontSize: 14 }}>
          {customer.brandName.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate"
            style={{ fontSize: "var(--text-base)", fontWeight: 600, color: "var(--foreground)" }}>
            {customer.brandName}
          </div>
          <div className="truncate flex items-center gap-2"
            style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            <span className="truncate">{customer.websiteUrl}</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline flex items-center gap-1">
              <Clock className="w-3 h-3 inline" />
              {new Date(customer.updatedAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 md:gap-4 flex-shrink-0 w-full md:w-auto">
        <div className="flex-1 md:flex-initial md:min-w-[200px]">
          <div className="flex items-center justify-between mb-1 gap-2">
            <span className="truncate"
              style={{ fontSize: "var(--text-xs)", color: "var(--foreground)", fontWeight: 600 }}>
              {stage?.title || "—"}
            </span>
            <span className="flex-shrink-0"
              style={{ fontSize: 10, color: "var(--muted-foreground)", fontWeight: 600 }}>
              {effective}/10
            </span>
          </div>
          <div className="w-full rounded-full overflow-hidden"
            style={{ height: 4, backgroundColor: "var(--muted)" }}>
            <div className="h-full transition-all" style={{ width: `${progressPct}%`, backgroundColor: accent }} />
          </div>
        </div>
        <ArrowRight className="w-4 h-4 hidden md:block flex-shrink-0"
          style={{ color: "var(--muted-foreground)" }} />
      </div>
    </button>
  );
}

function EmptyState({ onNewCustomer }: { onNewCustomer: () => void }) {
  return (
    <div className="text-center py-16 rounded-xl"
      style={{ backgroundColor: "var(--card)", border: "1px dashed var(--border)" }}>
      <Building2 className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--muted-foreground)" }} />
      <p style={{ fontSize: "var(--text-base)", color: "var(--foreground)", fontWeight: 600, margin: 0 }}>
        No customers yet
      </p>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 4 }}>
        Start onboarding the first customer to populate this list.
      </p>
      <button onClick={onNewCustomer}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg"
        style={{
          backgroundColor: "var(--alpha-teal)", color: "white",
          fontSize: "var(--text-sm)", fontWeight: 600, border: "none", cursor: "pointer",
        }}>
        <Plus className="w-4 h-4" />
        New customer
      </button>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const cfg: Record<string, { bg: string; fg: string; label: string }> = {
    active: { bg: "var(--alpha-teal-10)", fg: "var(--alpha-teal)", label: "Active" },
    in_progress: { bg: "var(--alpha-teal-10)", fg: "var(--alpha-teal)", label: "In progress" },
    completed: { bg: "rgba(34,197,94,0.1)", fg: "var(--chart-3)", label: "Complete" },
    paused: { bg: "var(--muted)", fg: "var(--muted-foreground)", label: "Paused" },
    failed: { bg: "rgba(220,38,38,0.1)", fg: "var(--infrared-coral)", label: "Failed" },
  };
  const c = cfg[status] || cfg.active;
  return (
    <span className="px-2 py-1 rounded text-xs"
      style={{ backgroundColor: c.bg, color: c.fg, fontWeight: 600 }}>
      {c.label}
    </span>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// CREATE VIEW
// ───────────────────────────────────────────────────────────────────────────

function CustomerCreateView({
  stages, onCancel, onCreated,
}: {
  stages: StageDefinition[];
  onCancel: () => void;
  onCreated: (id: number) => void;
}) {
  const stage1 = stages.find((s) => s.number === 1);

  const dummyCustomer: CustomerDetail = {
    id: 0, brand_name: "", website_url: "", competitors: [], one_liner: "",
    current_stage: 1, status: "in_progress",
    framework: { target_users: [], reasoning_variables: [], queries: [], name_variants: [] },
    framework_approved: false,
    created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
  };

  const handleSubmit = async (payload: IntakePayload) => {
    try {
      const res = await createCustomer(payload);
      toast.success(`Customer "${res.customer.brand_name}" created.`);
      onCreated(res.customer.id);
    } catch (e: any) {
      toast.error(e?.message || "Failed to create customer");
      throw e;
    }
  };

  if (!stage1) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--background)" }}>
      <div className="w-full px-4 md:px-8 py-6 md:py-10">
        <button onClick={onCancel} className="flex items-center gap-2 mb-4 px-3 py-1.5 rounded-md"
          style={{
            background: "transparent", color: "var(--muted-foreground)",
            fontSize: "var(--text-sm)", border: "1px solid var(--border)", cursor: "pointer",
          }}>
          <ChevronLeft className="w-4 h-4" />
          Back to customer list
        </button>
        <h1 style={{ fontSize: "var(--text-2xl)", fontWeight: 700, color: "var(--foreground)", margin: 0, marginBottom: 4 }}>
          New customer
        </h1>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginBottom: 24 }}>
          Start at Stage 1. The remaining 9 stages unlock as you progress.
        </p>
        <OnboardingStageContent
          customer={dummyCustomer} stage={stage1} isRunning={false}
          onCustomerUpdate={() => {}} onStageComplete={() => {}}
          onIntakeSubmit={handleSubmit}
        />
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// DETAIL VIEW
// ───────────────────────────────────────────────────────────────────────────

function CustomerDetailView({
  stages, customerId, onBack, onCompareRuns,
}: {
  stages: StageDefinition[];
  customerId: number;
  onBack: () => void;
  onCompareRuns: (baselineRunId: number, candidateRunId: number) => void;
}) {
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeStageNumber, setActiveStageNumber] = useState<number>(2);
  const [runningStage, setRunningStage] = useState<number | null>(null);
  const [stageErrors, setStageErrors] = useState<Record<number, string>>({});

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await getCustomer(customerId);
      setCustomer(c);
      // ⚠️ Existing customers are never at Stage 1 conceptually — intake is
      // implicit. Jump to whichever stage they're actually working on, or
      // Stage 2 at minimum.
      setActiveStageNumber(effectiveStageNumeric(c.current_stage));
    } catch (e: any) {
      setError(e?.message || "Failed to load customer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const activeStage = useMemo(
    () => stages.find((s) => s.number === activeStageNumber),
    [stages, activeStageNumber],
  );

  if (loading) return <FullPageSpinner label="Loading customer…" />;
  if (error || !customer || !activeStage) {
    return (
      <div className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--background)" }}>
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--infrared-coral)" }} />
          <p style={{ color: "var(--infrared-coral)", fontSize: "var(--text-sm)" }}>
            {error || "Customer not found"}
          </p>
          <button onClick={onBack} className="mt-3 px-4 py-2 rounded-md"
            style={{
              backgroundColor: "var(--muted)", color: "var(--foreground)",
              fontSize: "var(--text-sm)", border: "1px solid var(--border)", cursor: "pointer",
            }}>
            Back
          </button>
        </div>
      </div>
    );
  }

  // ⚠️ Effective current stage — bumped to 2 for legacy customers at Stage 1.
  // Used by the stepper and click-permissions.
  const effectiveCurrent = effectiveStageNumeric(customer.current_stage);

  // Navigation ceiling: the user can always return to any stage up to the
  // furthest they've actually reached — max(backend current_stage, the stage
  // they're viewing). This keeps navigation working even if a stuck or
  // mid-flight pipeline leaves customer.current_stage momentarily behind the
  // run-history stage (Stage 11 is on-demand, post-onboarding).
  const navCeiling = Math.max(effectiveCurrent, activeStageNumber);

  const executeStage = async (n: number) => {
    setRunningStage(n);
    setStageErrors((prev) => {
      if (!(n in prev)) return prev;
      const next = { ...prev };
      delete next[n];
      return next;
    });
    try {
      if (n === 2 || n === 4 || n === 5 || n === 6) {
        // Stage 2 (framework generation, #136), Stage 4 (measurement), Stage 5
        // (scoring) and Stage 6 (human review) are all self-managed by their
        // stage components. The global execute
        // button is not rendered for them (see renderActionButton). Nothing
        // to do here.
        return;
      } else if (n === 7) {
        const res = await runStage7(customer.id);
        setCustomer(res.customer);
        toast.success("Diagnostic complete.");
        setActiveStageNumber(8);
      } else if (n === 8) {
        const res = await runStage8(customer.id);
        setCustomer(res.customer);
        toast.success("Baseline report generated.");
        setActiveStageNumber(9);
      } else if (n === 9) {
        const res = await runStage9(customer.id);
        setCustomer(res.customer);
        toast.success("Blueprint generated.");
        setActiveStageNumber(10);
      }
    } catch (e: any) {
      const msg = e?.message || `Stage ${n} failed`;
      toast.error(msg);
      setStageErrors((prev) => ({ ...prev, [n]: msg }));
    } finally {
      if (n !== 2 && n !== 4 && n !== 5 && n !== 6) setRunningStage(null);
    }
  };

  const handleStageComplete = () => {
    setRunningStage(null);
    reload();
    if (activeStageNumber < 10) setActiveStageNumber(activeStageNumber + 1);
  };

  const handleCustomerUpdate = (c: CustomerDetail) => setCustomer(c);

  const buttonForStage = renderActionButton({
    stage: activeStage,
    customer,
    running: runningStage === activeStage.number,
    onExecute: () => executeStage(activeStage.number),
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--background)" }}>
      <div className="w-full px-4 md:px-8 py-6 md:py-8">

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 rounded-md"
              style={{
                background: "transparent", color: "var(--muted-foreground)",
                border: "1px solid var(--border)", cursor: "pointer",
              }}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 style={{ fontSize: "var(--text-xl)", fontWeight: 700, color: "var(--foreground)", margin: 0 }}>
                {customer.brand_name}
              </h1>
              <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginTop: 2 }}>
                {customer.website_url} · Stage {effectiveCurrent}/10
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={customer.status} />
            <button onClick={reload}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md"
              style={{
                backgroundColor: "var(--muted)", color: "var(--foreground)",
                fontSize: "var(--text-xs)", fontWeight: 500,
                border: "1px solid var(--border)", cursor: "pointer",
              }}>
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
          </div>
        </div>

        <div className="mb-6">
          <OnboardingStepper
            stages={stages}
            currentStage={effectiveCurrent}
            customerStatus={customer.status}
            runningStage={runningStage}
            maxNavigableStage={navCeiling}
            onStageClick={(n) => {
              if (n <= navCeiling || customer.status === "completed") {
                setActiveStageNumber(n);
              }
            }}
          />
        </div>

        <OnboardingStageContent
          customer={customer}
          stage={activeStage}
          isRunning={runningStage === activeStage.number}
          stageError={stageErrors[activeStage.number] ?? null}
          onCustomerUpdate={handleCustomerUpdate}
          onStageComplete={handleStageComplete}
          onCompareRuns={onCompareRuns}
        />

        {buttonForStage && (
          <div className="sticky bottom-0 mt-6 -mx-4 md:-mx-8 px-4 md:px-8 py-4"
            style={{ backgroundColor: "var(--background)", borderTop: "1px solid var(--border)" }}>
            <div className="w-full flex justify-end">
              {buttonForStage}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function renderActionButton(args: {
  stage: StageDefinition;
  customer: CustomerDetail;
  running: boolean;
  onExecute: () => void;
}): React.ReactNode {
  const { stage, customer, running, onExecute } = args;

  if (stage.number === 1) return null;
  // Stage 2 owns its generate / regenerate buttons (#136).
  if (stage.number === 2) return null;
  if (stage.number === 3) return null;
  // ⚠️ Stage 5 (scoring) and Stage 6 (human review) are now self-managed —
  // each owns its own run / sample / compute buttons inside its stage
  // component (like Stage 10 brand monitoring and Stage 11 run history).
  // Returning null here removes the dead bottom "Start scoring" /
  // "Run validation" button that would otherwise do nothing.
  if (stage.number === 5 || stage.number === 6) return null;
  if (stage.number === 10 || stage.number === 11) return null;

  if (stage.number === 4 && !customer.framework_approved) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
        style={{
          backgroundColor: "rgba(245,158,11,0.08)",
          border: "1px solid var(--chart-3)",
        }}>
        <Lock className="w-3.5 h-3.5" style={{ color: "var(--chart-3)" }} />
        <span style={{
          fontSize: "var(--text-xs)",
          color: "var(--foreground)",
          fontWeight: 500,
        }}>
          Approve the framework in <strong>Stage 3</strong> before starting measurement.
        </span>
      </div>
    );
  }

  const isDoneFor = (n: number) => {
    if (n === 2) {
      return (
        (customer.framework?.reasoning_variables?.length ?? 0) > 0 ||
        (customer.framework?.queries?.length ?? 0) > 0
      );
    }
    if (n === 4) return !!customer.measurement_run_id;
    if (n === 7)
      return (
        customer.gpt_zero != null &&
        Object.keys(customer.gpt_zero as any).length > 0
      );
    if (n === 8)
      return (
        customer.baseline_report != null &&
        Object.keys(customer.baseline_report as any).length > 0
      );
    if (n === 9)
      return (
        customer.blueprint != null &&
        Object.keys(customer.blueprint as any).length > 0
      );
    return false;
  };

  if (isDoneFor(stage.number)) {
    return (
      <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
        Stage complete — pick the next stage from the stepper above.
      </span>
    );
  }

  const labelByStage: Record<number, string> = {
    2: "Generate framework",
    4: "Start measurement run",
    7: "Run diagnostic probe",
    8: "Generate baseline report",
    9: "Generate blueprint",
    10: "Run AI brand monitoring",
  };

  return (
    <ExecuteButton
      label={labelByStage[stage.number] || "Execute stage"}
      running={running}
      onClick={onExecute}
    />
  );
}

function ExecuteButton({
  label, running, onClick,
}: {
  label: string; running: boolean; onClick: () => void;
}) {
  return (
    <button onClick={onClick} disabled={running}
      className="flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all"
      style={{
        backgroundColor: running ? "var(--muted)" : "var(--alpha-teal)",
        color: running ? "var(--muted-foreground)" : "white",
        fontSize: "var(--text-sm)", fontWeight: 600,
        border: "none",
        cursor: running ? "not-allowed" : "pointer",
      }}>
      {running ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
      {label}
      {!running && <ArrowRight className="w-4 h-4" />}
    </button>
  );
}

function FullPageSpinner({ label, inline }: { label: string; inline?: boolean }) {
  return (
    <div className={
        inline ? "flex items-center justify-center py-12"
               : "min-h-screen flex items-center justify-center"
      }
      style={inline ? {} : { backgroundColor: "var(--background)" }}>
      <div className="flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--alpha-teal)" }} />
        <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
          {label}
        </span>
      </div>
    </div>
  );
}